import {
  Route,
  createRoutesFromChildren,
  matchRoutes,
  Routes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

// Backstage Core
import { createApp } from '@backstage/frontend-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import {
  convertLegacyAppOptions,
  convertLegacyAppRoot,
} from '@backstage/core-compat-api';
import { oktaAuthApiRef } from '@backstage/core-plugin-api';
import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';

// Backstage Plugins
import {
  ApiExplorerPage,
  DefaultApiExplorerPage,
} from '@backstage/plugin-api-docs';
import { CatalogEntityPage, CatalogIndexPage } from '@backstage/plugin-catalog';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { CatalogUnprocessedEntitiesPage } from '@backstage/plugin-catalog-unprocessed-entities';
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsReaderPage,
  TechDocsReaderLayout,
  TechDocsIndexPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import {
  LightBox,
  ReportIssue,
} from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';

// Community / Third-party Plugins
import { FossaPage } from '@backstage-community/plugin-fossa';
import { AnnouncementsPage } from '@backstage-community/plugin-announcements';

// Internal Plugins
import { MaturityPage } from '@internal/plugin-maturity';
import { PackageDependenciesPage } from '@internal/plugin-packages';

// Components
import { apis } from './apis';
import { Root } from './components/Root';
import {
  MedidataCatalogPage,
  apiColumns,
} from './components/catalog/CatalogPage';
import { entityPage } from './components/catalog/EntityPage';
import { ExplorePage } from './components/explore/ExplorePage';
import { HomePage } from './components/home/HomePage';
import { searchPage } from './components/search/SearchPage';
import { MedidataTechDocsHome } from './components/techdocs/TechDocsHome';

// Frontend Instrumentation
import {
  getWebInstrumentations,
  initializeFaro,
  ReactIntegration,
  createReactRouterV6Options,
  FaroErrorBoundary,
} from '@grafana/faro-react';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';

if (!window.location.hostname.includes('localhost')) {
  initializeFaro({
    url: 'https://faro-collector-prod-us-east-2.grafana.net/collect/85a74a13fbe8b2c5730aed5492cc0e0b',
    app: {
      name: 'devopscentralplatform',
      version: '1.0.0',
      environment: 'sandbox',
    },

    instrumentations: [
      // Load the default Web instrumentations
      ...getWebInstrumentations(),

      // Tracing Instrumentation is needed if you want to use the React Profiler
      new TracingInstrumentation({
        instrumentationOptions: {
          propagateTraceHeaderCorsUrls: [
            /faro-collector-prod-us-east-2\.grafana\.net/,
          ],
        },
      }),

      new ReactIntegration({
        // Only needed if you want to use the React Router instrumentation
        router: createReactRouterV6Options({
          createRoutesFromChildren,
          matchRoutes,
          Routes,
          useLocation,
          useNavigationType,
        }),
      }),
    ],
    sessionTracking: {
      samplingRate: 1,
      persistent: true,
    },
  });
}

const convertedOptionsModule = convertLegacyAppOptions({
  apis,
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        provider={{
          id: 'okta-auth-provider',
          title: 'Okta SSO',
          message: 'Sign in using your Medidata Okta account',
          apiRef: oktaAuthApiRef,
        }}
      />
    ),
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    <Route path="/catalog" element={<CatalogIndexPage />}>
      <MedidataCatalogPage />
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />}>
      <DefaultApiExplorerPage columns={apiColumns} />
    </Route>
    <Route path="/explore" element={<ExplorePage />}>
      <ExplorePage />
    </Route>
    <Route path="/maturity" element={<MaturityPage />}>
      <MaturityPage />
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />}>
      <MedidataTechDocsHome />
    </Route>
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        {/* TODO(jvanalstyne-mdsol): Replace lineColor below with theme-compatible value */}
        <Mermaid
          config={{ theme: 'forest', themeVariables: { lineColor: '#000000' } }}
        />
        <LightBox />
        <ReportIssue />
      </TechDocsAddons>
      <TechDocsReaderLayout withSearch withHeader={false} />
    </Route>
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/fossa" element={<FossaPage />} />
    <Route path="/packagedependencies" element={<PackageDependenciesPage />} />
    <Route
      path="/mcatalog-graph"
      element={
        <CatalogGraphPage
          initialState={{
            rootEntityRefs: ['group:medidata'],
            maxDepth: 1,
            showFilters: false,
            selectedKinds: [
              'component',
              'domain',
              'system',
              'user',
              'group',
              'api',
            ],
          }}
        />
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route
      path="/catalog-unprocessed-entities"
      element={<CatalogUnprocessedEntitiesPage />}
    />
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/announcements" element={<AnnouncementsPage />} />
  </FlatRoutes>
);

const convertedRootFeatures = convertLegacyAppRoot(
  <FaroErrorBoundary>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </FaroErrorBoundary>,
);

export default createApp({
  features: [convertedOptionsModule, ...convertedRootFeatures],
});
